import {
  cargoAmountToSystemUnit,
  convertStowageFactorToMetric,
  resolveCargoUnitQuantity,
  type PhysicalQuantity,
} from '../../constants/cargoUnits';
import type { Assortment, Maybe } from '../../graphql';
import { CargoType, MarginUnit } from '../../graphql';
import { isDefined } from '../../lib/util';
import type { Order } from './Core';
import { stringToNumOpt } from './util';

export type CargoRangeInputs = Pick<
  Order,
  'cargoUnitByCargoUnitId' | 'marginUnit'
> & {
  assortmentByAssortmentId?: Maybe<
    Pick<
      Assortment,
      | 'cargoType'
      | 'marginUnit'
      | 'marginAmount'
      | 'specificGravity'
      | 'stowageFactorMin'
      | 'stowageFactorMax'
      | 'fillFactor'
    >
  >;
  cargoAmount?: null | number | string;
  marginAmount?: null | number | string;
  specificGravity?: null | number | string;
  stowageFactorMin?: null | number | string;
  stowageFactorMax?: null | number | string;
};

export function calculateMassAndVolumeRanges(
  order: CargoRangeInputs | undefined,
  ignoreMargins: boolean,
  defaultFillFactor?: string | number | null,
): CargoRanges {
  if (!order) {
    return { mass: { min: 0, max: 0 }, volume: { min: 0, max: 0 } };
  }
  const {
    cargoUnitByCargoUnitId: cargoUnit,
    assortmentByAssortmentId: assortment,
    marginUnit = assortment?.marginUnit,
  } = order;
  const fillFactor = Number(assortment?.fillFactor ?? defaultFillFactor ?? 1);
  const cargoAmount = Number(order.cargoAmount ?? 0);
  const marginAmount = ignoreMargins
    ? 0
    : Number(order.marginAmount ?? assortment?.marginAmount ?? 0);

  const specificGravityIn =
    order.specificGravity ?? assortment?.specificGravity;
  const specificGravity = stringToNumOpt(specificGravityIn) ?? null;

  const stowageFactorMin = stringToNumOpt(
    order.stowageFactorMin ?? assortment?.stowageFactorMin,
  );
  const stowageFactorMax = stringToNumOpt(
    order.stowageFactorMax ?? assortment?.stowageFactorMax,
  );

  const cargoType = assortment?.cargoType ?? CargoType.SOLID;

  let maxAmount, minAmount;
  switch (marginUnit) {
    case MarginUnit.MINMAX: {
      // When MINMAX is given the cargo amount is the MIN and margin amount is the MAX,
      // but if margin amount isn't given let's use cargo amount for both
      minAmount = cargoAmount;
      maxAmount = marginAmount || cargoAmount;
      break;
    }
    case MarginUnit.PERCENTAGE: {
      minAmount = cargoAmount - (marginAmount * cargoAmount) / 100;
      maxAmount = cargoAmount + (marginAmount * cargoAmount) / 100;
      break;
    }
    default:
    case MarginUnit.TONNES: {
      minAmount = cargoAmount - marginAmount;
      maxAmount = cargoAmount + marginAmount;
      break;
    }
  }

  minAmount = cargoUnit?.name
    ? cargoAmountToSystemUnit(minAmount, cargoUnit.name)
    : minAmount;
  maxAmount = cargoUnit?.name
    ? cargoAmountToSystemUnit(maxAmount, cargoUnit.name)
    : maxAmount;

  const quantity: PhysicalQuantity = cargoUnit?.name
    ? resolveCargoUnitQuantity(cargoUnit.name, {
        stowageFactorMin,
        stowageFactorMax,
        specificGravity,
        cargoType,
      })
    : 'mass';
  const otherQuantity: PhysicalQuantity =
    quantity === 'mass' ? 'volume' : 'mass';

  const ranges = {
    mass: { min: 0, max: 0 },
    volume: { min: 0, max: 0 },
  };

  ranges[quantity].min = minAmount;
  ranges[quantity].max = maxAmount;

  switch (cargoType) {
    case 'SOLID': {
      if (isDefined(stowageFactorMin) || isDefined(stowageFactorMax)) {
        ranges[otherQuantity] = getAmountByStowageFactor(
          { min: minAmount, max: maxAmount, quantity },
          {
            min: stowageFactorMin ?? stowageFactorMax!,
            max: stowageFactorMax ?? stowageFactorMin!,
          },
        );
      } else if (specificGravity !== null) {
        ranges[otherQuantity] = getAmountBySpecificGravity(
          { min: minAmount, max: maxAmount, quantity },
          specificGravity,
        );
      }
      break;
    }
    case 'GAS':
    case 'LIQUID': {
      if (specificGravity !== null) {
        ranges[otherQuantity] = getAmountBySpecificGravity(
          { min: minAmount, max: maxAmount, quantity },
          specificGravity,
        );
      } else if (isDefined(stowageFactorMin) || isDefined(stowageFactorMax)) {
        ranges[otherQuantity] = getAmountByStowageFactor(
          { min: minAmount, max: maxAmount, quantity },
          {
            min: stowageFactorMin ?? stowageFactorMax!,
            max: stowageFactorMax ?? stowageFactorMin!,
          },
        );
      }

      break;
    }
  }

  // Apply fillFactor to volume calculations
  ranges.volume.min *= fillFactor;
  ranges.volume.max *= fillFactor;

  return ranges;
}
export type MassVolumeSize = {
  mass: number;
  volume: number;
};
/** Add all sizes together */

export const sumMassVolumeSize = (sizes: MassVolumeSize[]): MassVolumeSize => {
  const result: MassVolumeSize = {
    mass: 0,
    volume: 0,
  };

  for (const size of sizes) {
    result.mass += size.mass;
    result.volume += size.volume;
  }
  return result;
};
/** Subtract from the first size all the rest */

export const subtractMassVolumeSize = (
  size1: MassVolumeSize,
  ...restSizes: MassVolumeSize[]
): MassVolumeSize => {
  const result: MassVolumeSize = {
    ...size1,
  };

  for (const size of restSizes) {
    result.mass -= size.mass;
    result.volume -= size.volume;
  }
  return result;
};

export function cargoRangeFits(
  cargo: CargoRanges,
  containerSize: MassVolumeSize,
): boolean {
  return (
    cargo.mass.min <= containerSize.mass &&
    cargo.volume.min <= containerSize.volume
  );
}
export function getAmountByStowageFactor(
  amount: { min: number; max: number; quantity: 'mass' | 'volume' },
  stowageFactor: { min: number; max: number },
) {
  const stowageFactorMin = convertStowageFactorToMetric(stowageFactor.min);
  const stowageFactorMax = convertStowageFactorToMetric(stowageFactor.max);

  let min = 0;
  let max = 0;

  switch (amount.quantity) {
    case 'mass': {
      min = amount.min * stowageFactorMin;
      max = amount.max * stowageFactorMax;
      break;
    }
    case 'volume': {
      min = stowageFactorMax !== 0 ? amount.min / stowageFactorMax : 0;
      max = stowageFactorMin !== 0 ? amount.max / stowageFactorMin : 0;
      break;
    }
  }

  return {
    min,
    max,
  };
}

/**
 * Calculates the mass or volume of a cargo given its mass or volume range
 * and specific gravity.
 * @param amount The range of mass or volume of the cargo.
 * @param specificGravity The specific gravity of the cargo.
 * @returns An object with the calculated mass or volume range.
 */
export function getAmountBySpecificGravity(
  amount: { min: number; max: number; quantity: 'mass' | 'volume' },
  specificGravity: number,
) {
  let min = 0;
  let max = 0;

  switch (amount.quantity) {
    case 'mass': {
      if (specificGravity === 0) break;
      min = amount.min / specificGravity;
      max = amount.max / specificGravity;
      break;
    }
    case 'volume': {
      min = amount.min * specificGravity;
      max = amount.max * specificGravity;
      break;
    }
  }

  return {
    min,
    max,
  };
}
export function midPoint(range: CargoRange): number {
  return 0.5 * (range.min + range.max);
}
export type CargoRange = { min: number; max: number };

export interface CargoRanges {
  mass: CargoRange;
  volume: CargoRange;
}
